import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";

import { ConfirmationCheckbox, SelectInput, TextInput } from "@/shared/components";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import type { StructuredDocumentData } from "@/shared/types/documentuploadtype";
import { formatEmiratesId } from "@/shared/utils/emiratesId";
import { createCarAdditionalInfoSchema } from "@/shared/validation/carQuoteSchemas";
import { defaultAdditionalInfoValues } from "../../redux/car-quote/carQuoteSlice";
import type { CarAdditionalInfoValues } from "../../redux/type";
import {
  getCarAdditionalInfoOcrDefaults,
  mergeCarAdditionalInfoOcrDefaults,
} from "../../utils/carAdditionalInfoOcr";

const mileageOptions = [
  "Up to 10,000 km",
  "10,001 - 20,000 km",
  "20,001 - 30,000 km",
  "Above 30,000 km",
];

const registrationTypeOptions = ["Private", "Commercial", "Motorcycle"];

export const AdditionalInfoStep = forwardRef<
  StepFormHandle<CarAdditionalInfoValues>,
  StepFormComponentProps<
    CarAdditionalInfoValues,
    {
      isReadOnly?: boolean;
      driverDateOfBirth?: string;
      ocrStructuredData?: StructuredDocumentData;
      onAuthorizationConfirmChange?: (checked: boolean) => boolean | Promise<boolean> | void;
    }
  >
>(function AdditionalInfoStep(
  {
    defaultValues,
    isReadOnly = false,
    driverDateOfBirth,
    ocrStructuredData,
    onAuthorizationConfirmChange,
  },
  ref
) {
  const additionalInfoSchema = useMemo(
    () => createCarAdditionalInfoSchema(driverDateOfBirth),
    [driverDateOfBirth]
  );
  const ocrDefaults = useMemo(
    () => getCarAdditionalInfoOcrDefaults(ocrStructuredData),
    [ocrStructuredData]
  );
  const resolvedDefaultValues = useMemo(() => {
    const mergedValues = {
      ...defaultAdditionalInfoValues,
      ...defaultValues,
    };

    return mergeCarAdditionalInfoOcrDefaults(mergedValues, ocrDefaults);
  }, [defaultValues, ocrDefaults]);
  const form = useForm<CarAdditionalInfoValues>({
    resolver: zodResolver(additionalInfoSchema) as Resolver<CarAdditionalInfoValues>,
    defaultValues: resolvedDefaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    form.reset(resolvedDefaultValues);
  }, [form, resolvedDefaultValues]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      form.setValue("emiratesId", formatEmiratesId(form.getValues("emiratesId")), {
        shouldDirty: true,
        shouldValidate: true,
      });

      return form.trigger(undefined, { shouldFocus: true });
    },
    getValues: () => form.getValues(),
  }));

  return (
    <div className="space-y-6">
      <fieldset disabled={isReadOnly} className="contents">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SelectInput
            control={form.control}
            name="estimatedAnnualMileage"
            label="Estimated Annual Mileage"
            placeholder="Select Annual Mileage"
            required
            options={mileageOptions.map((option) => ({
              label: option,
              value: option,
            }))}
          />
          <TextInput
            control={form.control}
            name="chassisNumber"
            label="Chassis Number"
            placeholder="Enter Chassis Number"
            required
          />
          <TextInput
            control={form.control}
            name="emiratesId"
            label="Emirates ID"
            placeholder="784-XXXX-XXXXXXX-X"
            formatValueOnBlur={formatEmiratesId}
            inputMode="numeric"
            maxLength={18}
            required
          />
          <TextInput
            control={form.control}
            name="carFinancedBy"
            label="Car Financed By"
            placeholder="Search Car Financed By"
            required
          />
          <TextInput
            control={form.control}
            name="vehicleColor"
            label="Vehicle Color"
            placeholder="Search Vehicle Color"
            required
          />
          <TextInput
            control={form.control}
            name="tcfNumber"
            label="TCF No."
            placeholder="Enter TCF No."
            required
          />
          <SelectInput
            control={form.control}
            name="registrationType"
            label="Registration Type"
            placeholder="Select Registration Type"
            required
            options={registrationTypeOptions.map((option) => ({
              label: option,
              value: option,
            }))}
          />
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-3">
            <TextInput
              control={form.control}
              name="plateCode"
              label="Plate Code"
              placeholder="Select Plate"
              required
            />
            <TextInput
              control={form.control}
              name="plateNumber"
              label="Plate Number"
              placeholder="Enter Plate Number"
              required
            />
          </div>
          <TextInput
            control={form.control}
            name="engineNumber"
            label="Engine Number"
            placeholder="Enter Engine Number"
            required
          />
        </div>
      </fieldset>

      {/* <Controller
        control={form.control}
        name="authorizationAccepted"
        render={({ field, fieldState }) => (
          <div>
            <label className="flex items-start gap-3">
              <Checkbox
                color="green"
                className="mt-1"
                checked={field.value === true}
                onChange={(event) => field.onChange(event.target.checked)}
              />
              <span className="text-sm text-theme-text">
                I agree to customer authorization, PROTEGO terms and conditions and Liva Insurance
                B.S.C (c) Terms & conditions
              </span>
            </label>
            {fieldState.error?.message ? (
              <p className="mt-2 text-sm font-medium text-red-600">{fieldState.error.message}</p>
            ) : null}
          </div>
        )}
      /> */}
      <fieldset disabled={isReadOnly} className="contents">
        <ConfirmationCheckbox
          checked={form.watch("authorizationAccepted") === true}
          disabled={isReadOnly}
          error={form.formState.errors.authorizationAccepted?.message ?? ""}
          onChange={async (checked) => {
            if (checked) {
              form.setValue("authorizationAccepted", true, {
                shouldValidate: true,
                shouldDirty: true,
              });
              const isValid = await form.trigger(undefined, { shouldFocus: true });
              if (!isValid) {
                form.setValue("authorizationAccepted", false, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                return;
              }
            } else {
              form.setValue("authorizationAccepted", false, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }

            queueMicrotask(async () => {
              const accepted = await onAuthorizationConfirmChange?.(checked);
              if (checked && accepted === false) {
                form.setValue("authorizationAccepted", false, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }
            });
          }}
        >
          I agree to the Customer Authorization and Terms & Conditions.
        </ConfirmationCheckbox>
      </fieldset>
    </div>
  );
});
